import React, {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import type { User } from "../types";
import { getCurrentUser } from "../services/api";
import userService from "../services/userService";

interface AuthContextProps {
    user: User | null;
    isInitializing: boolean;
    login: (authToken: string, refreshToken: string) => Promise<void>;
    logout: () => void;
    refreshUser: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isInitializing, setIsInitializing] = useState(true);

    const refreshUser = useCallback(async (): Promise<User | null> => {
        if (!userService.getAuthToken()) {
            setUser(null);
            return null;
        }

        const currentUser = await getCurrentUser();
        setUser(currentUser);
        return currentUser;
    }, []);

    useEffect(() => {
        let isActive = true;

        const initialize = async () => {
            if (
                !userService.getAuthToken() ||
                !userService.getRefreshToken()
            ) {
                if (isActive) {
                    setIsInitializing(false);
                }
                return;
            }

            try {
                const currentUser = await getCurrentUser();
                if (isActive) {
                    setUser(currentUser);
                }
            } catch {
                userService.logout();
                if (isActive) {
                    setUser(null);
                }
            } finally {
                if (isActive) {
                    setIsInitializing(false);
                }
            }
        };

        void initialize();

        return () => {
            isActive = false;
        };
    }, []);

    const login = useCallback(
        async (authToken: string, refreshToken: string): Promise<void> => {
            userService.login(authToken, refreshToken);
            try {
                await refreshUser();
            } catch (error) {
                userService.logout();
                setUser(null);
                throw error;
            }
        },
        [refreshUser],
    );

    const logout = useCallback(() => {
        userService.logout();
        setUser(null);
        window.location.assign("/login");
    }, []);

    const value = useMemo(
        () => ({
            user,
            isInitializing,
            login,
            logout,
            refreshUser,
        }),
        [isInitializing, login, logout, refreshUser, user],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextProps => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
